import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  AssistantAccessArea,
  WorkspaceAssistantConfiguration,
  WorkspaceAssistantOverride,
} from '@/api/assistantConfiguration/assistantConfigurationApi.types';
import { useSaveTenantAssistantConfigurationMutation } from '@/hooks/useAssistantConfiguration';
import { useHasPlatformPermission } from '@/hooks/useHasPlatformPermission';
import { AccessAreaList } from './AccessAreaList';
import { BehaviourFields } from './BehaviourFields';
import { ConfigurationCard } from './ConfigurationCard';
import { InstructionsField } from './InstructionsField';
import { MonthlyBudgetField } from './MonthlyBudgetField';
import { OptionChips } from './OptionChips';
import { PlatformInstructionsPreview } from './PlatformInstructionsPreview';
import { ReadOnlyNotice } from './ReadOnlyNotice';
import { SaveBar } from './SaveBar';
import { toggleAccessArea } from './assistantConfigurationOptions';
import { normaliseInstructions } from './normaliseInstructions';
import { useConfigurationSaveNotifications } from './useConfigurationSaveNotifications';
import { ProactiveFeaturesField } from './ProactiveFeaturesField';
import { useSyncedDraft } from './useSyncedDraft';

const UPDATE_PERMISSION = 'assistant_configuration.update';

interface TenantAssistantConfigurationFormProps {
  tenantId: string;
  configuration: WorkspaceAssistantConfiguration;
}

export function TenantAssistantConfigurationForm({
  tenantId,
  configuration,
}: TenantAssistantConfigurationFormProps) {
  const { t } = useTranslation('assistant');
  const canUpdate = useHasPlatformPermission(UPDATE_PERMISSION);
  const saveMutation = useSaveTenantAssistantConfigurationMutation(tenantId);
  const notifications = useConfigurationSaveNotifications();
  const platform = configuration.platform;
  const { draft, setDraft, baseRevision, adoptSaved } = useSyncedDraft<WorkspaceAssistantOverride>(
    configuration.workspace,
    configuration.workspaceRevision
  );
  const instructions = draft.extraInstructions === null ? '' : draft.extraInstructions;
  const nextOverride: WorkspaceAssistantOverride = {
    ...draft,
    extraInstructions: normaliseInstructions(instructions),
  };
  const hasChanges = JSON.stringify(nextOverride) !== JSON.stringify(configuration.workspace);
  const ceilingAreas =
    draft.allowedAccessAreas === null ? platform.allowedAccessAreas : draft.allowedAccessAreas;

  function submit(formEvent: FormEvent<HTMLFormElement>) {
    formEvent.preventDefault();
    saveMutation.mutate(
      { value: nextOverride, expectedRevision: baseRevision },
      {
        onSuccess: function adoptAndNotify(saved) {
          adoptSaved(saved.workspace, saved.workspaceRevision);
          notifications.onSuccess();
        },
        onError: notifications.onError,
      }
    );
  }

  function switchAreaBackOn(area: AssistantAccessArea) {
    setDraft({
      ...draft,
      disabledAccessAreas: draft.disabledAccessAreas.filter(function keepOthers(disabledArea) {
        return disabledArea !== area;
      }),
    });
  }

  return (
    <form onSubmit={submit}>
      <fieldset disabled={canUpdate === false} className="space-y-5">
        {canUpdate === false ? <ReadOnlyNotice /> : null}
        <ConfigurationCard title={t('configuration.behaviourTitle')}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <BehaviourFields
              tone={draft.tone}
              replyLength={draft.replyLength}
              inheritedTone={platform.tone}
              inheritedReplyLength={platform.replyLength}
              onToneChange={function updateTone(nextTone) {
                setDraft({ ...draft, tone: nextTone });
              }}
              onReplyLengthChange={function updateReplyLength(nextReplyLength) {
                setDraft({ ...draft, replyLength: nextReplyLength });
              }}
            />
            <div className="space-y-4">
              <PlatformInstructionsPreview instructions={platform.extraInstructions} />
              <InstructionsField
                fieldId="tenant-assistant-instructions"
                value={instructions}
                hint={t('configuration.instructions.workspaceHint')}
                onChange={function updateInstructions(nextInstructions) {
                  setDraft({ ...draft, extraInstructions: nextInstructions });
                }}
              />
            </div>
          </div>
        </ConfigurationCard>

        <ConfigurationCard
          title={t('configuration.areas.title')}
          hint={t('configuration.areas.hint')}
        >
          <div className="mb-4">
            <OptionChips
              label={t('configuration.areas.title')}
              isLabelHidden
              options={[
                { value: true, label: t('configuration.areas.useDefault') },
                { value: false, label: t('configuration.areas.customForWorkspace') },
              ]}
              selectedValue={draft.allowedAccessAreas === null}
              onSelect={function selectCeilingMode(followsPlatform) {
                setDraft({
                  ...draft,
                  allowedAccessAreas:
                    followsPlatform === true ? null : [...platform.allowedAccessAreas],
                });
              }}
            />
          </div>
          <AccessAreaList
            resolveRowState={function resolveRowState(area) {
              const isSwitchedOffByWorkspace =
                ceilingAreas.includes(area) && draft.disabledAccessAreas.includes(area);
              return {
                isOn: ceilingAreas.includes(area),
                isLocked: draft.allowedAccessAreas === null,
                note: isSwitchedOffByWorkspace
                  ? t('configuration.areas.switchedOffByWorkspace')
                  : null,
                noteActionLabel: isSwitchedOffByWorkspace
                  ? t('configuration.areas.switchBackOn')
                  : null,
              };
            }}
            onToggle={function toggleArea(area) {
              setDraft({ ...draft, allowedAccessAreas: toggleAccessArea(ceilingAreas, area) });
            }}
            onNoteAction={switchAreaBackOn}
          />
        </ConfigurationCard>

        <ConfigurationCard
          title={t('configuration.delegation.title')}
          hint={t('configuration.delegation.workspaceHint')}
        >
          <OptionChips
            label={t('configuration.delegation.workspaceLabel')}
            options={[
              {
                value: null,
                label: t('configuration.defaultOption', {
                  value:
                    platform.ownersCanCustomize === true
                      ? t('configuration.delegation.allowed')
                      : t('configuration.delegation.notAllowed'),
                }),
              },
              { value: true, label: t('configuration.delegation.allowed') },
              { value: false, label: t('configuration.delegation.notAllowed') },
            ]}
            selectedValue={draft.ownerCanCustomize}
            onSelect={function selectDelegation(nextValue) {
              setDraft({ ...draft, ownerCanCustomize: nextValue });
            }}
          />
        </ConfigurationCard>

        <ConfigurationCard
          title={t('configuration.proactive.title')}
          hint={t('configuration.proactive.hint')}
        >
          <ProactiveFeaturesField
            value={draft.proactiveFeaturesEnabled}
            inheritedValue={platform.proactiveFeaturesEnabled}
            onChange={function selectProactive(nextValue) {
              setDraft({ ...draft, proactiveFeaturesEnabled: nextValue });
            }}
          />
        </ConfigurationCard>

        <ConfigurationCard
          title={t('configuration.budget.title')}
          hint={t('configuration.budget.hint')}
        >
          <MonthlyBudgetField
            budget={draft.monthlyTokenBudget}
            defaultBudget={configuration.defaultMonthlyTokenBudget}
            onChange={function updateBudget(nextBudget) {
              setDraft({ ...draft, monthlyTokenBudget: nextBudget });
            }}
          />
        </ConfigurationCard>

        {canUpdate ? <SaveBar hasChanges={hasChanges} isSaving={saveMutation.isPending} /> : null}
      </fieldset>
    </form>
  );
}
